/**
 * useDataGridSelection
 *
 * Manages active cell state, click-to-select, and provides navigation helpers
 * for moving between editable cells in the grid.
 */

import { useState, useMemo, useCallback } from 'react';
import { READONLY_SYSTEM_FIELDS, type CellPosition, type DataGridSelectionOptions } from './types';

export interface UseDataGridSelectionReturn {
  activeCell: CellPosition | null;
  selectCell: (pos: CellPosition) => void;
  clearSelection: () => void;
  isCellActive: (rowIndex: number, columnId: string) => boolean;
  isCellEditable: (columnId: string) => boolean;
  editableColumnIds: string[];
  // Navigation helpers
  getNextEditableCell: (from: CellPosition) => CellPosition | null;
  getPrevEditableCell: (from: CellPosition) => CellPosition | null;
  getCellBelow: (from: CellPosition) => CellPosition | null;
  getCellAbove: (from: CellPosition) => CellPosition | null;
  getFirstInRow: (rowIndex: number) => CellPosition | null;
  getLastInRow: (rowIndex: number) => CellPosition | null;
  // Extended navigation (Ctrl+Home/End, PageUp/Down)
  getFirstCellInTable: () => CellPosition | null;
  getLastCellInTable: () => CellPosition | null;
  getCellPageDown: (from: CellPosition, pageSize: number) => CellPosition | null;
  getCellPageUp: (from: CellPosition, pageSize: number) => CellPosition | null;
}

export function useDataGridSelection(
  opts: DataGridSelectionOptions,
): UseDataGridSelectionReturn {
  const [activeCell, setActiveCell] = useState<CellPosition | null>(null);

  // Compute the ordered list of editable column IDs (stable reference)
  const editableColumnIds = useMemo(
    () =>
      opts.visibleColumnIds.filter(
        (id) => opts.editableFields.has(id) && !READONLY_SYSTEM_FIELDS.has(id),
      ),
    [opts.visibleColumnIds, opts.editableFields],
  );

  // ---------------------------------------------------------------------------
  // Core actions
  // ---------------------------------------------------------------------------

  const selectCell = useCallback(
    (pos: CellPosition) => {
      if (!opts.editableFields.has(pos.columnId)) return;
      if (READONLY_SYSTEM_FIELDS.has(pos.columnId)) return;
      if (pos.rowIndex < 0 || pos.rowIndex >= opts.rowCount) return;
      setActiveCell(pos);
      opts.onCellSelected?.(pos);
    },
    [opts.editableFields, opts.rowCount, opts.onCellSelected],
  );

  const clearSelection = useCallback(() => setActiveCell(null), []);

  const isCellActive = useCallback(
    (rowIndex: number, columnId: string) => {
      if (!activeCell) return false;
      return activeCell.rowIndex === rowIndex && activeCell.columnId === columnId;
    },
    [activeCell],
  );

  const isCellEditable = useCallback(
    (columnId: string) => {
      if (READONLY_SYSTEM_FIELDS.has(columnId)) return false;
      return opts.editableFields.has(columnId);
    },
    [opts.editableFields],
  );

  // ---------------------------------------------------------------------------
  // Navigation helpers
  // ---------------------------------------------------------------------------

  const getNextEditableCell = useCallback(
    (from: CellPosition): CellPosition | null => {
      if (editableColumnIds.length === 0) return null;

      const colIdx = editableColumnIds.indexOf(from.columnId);
      // Try next column in same row
      if (colIdx >= 0 && colIdx < editableColumnIds.length - 1) {
        return { rowIndex: from.rowIndex, columnId: editableColumnIds[colIdx + 1] };
      }
      // Wrap to first editable column of next row
      if (from.rowIndex < opts.rowCount - 1) {
        return { rowIndex: from.rowIndex + 1, columnId: editableColumnIds[0] };
      }
      // At the very end — no next cell
      return null;
    },
    [editableColumnIds, opts.rowCount],
  );

  const getPrevEditableCell = useCallback(
    (from: CellPosition): CellPosition | null => {
      if (editableColumnIds.length === 0) return null;

      const colIdx = editableColumnIds.indexOf(from.columnId);
      // Try prev column in same row
      if (colIdx > 0) {
        return { rowIndex: from.rowIndex, columnId: editableColumnIds[colIdx - 1] };
      }
      // Wrap to last editable column of prev row
      if (from.rowIndex > 0) {
        return {
          rowIndex: from.rowIndex - 1,
          columnId: editableColumnIds[editableColumnIds.length - 1],
        };
      }
      // At the very start — no prev cell
      return null;
    },
    [editableColumnIds],
  );

  const getCellBelow = useCallback(
    (from: CellPosition): CellPosition | null => {
      if (from.rowIndex >= opts.rowCount - 1) return null;
      // Same column, next row — only if column is editable
      if (!editableColumnIds.includes(from.columnId)) return null;
      return { rowIndex: from.rowIndex + 1, columnId: from.columnId };
    },
    [editableColumnIds, opts.rowCount],
  );

  const getCellAbove = useCallback(
    (from: CellPosition): CellPosition | null => {
      if (from.rowIndex <= 0) return null;
      if (!editableColumnIds.includes(from.columnId)) return null;
      return { rowIndex: from.rowIndex - 1, columnId: from.columnId };
    },
    [editableColumnIds],
  );

  const getFirstInRow = useCallback(
    (rowIndex: number): CellPosition | null => {
      if (editableColumnIds.length === 0) return null;
      return { rowIndex, columnId: editableColumnIds[0] };
    },
    [editableColumnIds],
  );

  const getLastInRow = useCallback(
    (rowIndex: number): CellPosition | null => {
      if (editableColumnIds.length === 0) return null;
      return { rowIndex, columnId: editableColumnIds[editableColumnIds.length - 1] };
    },
    [editableColumnIds],
  );

  // ---------------------------------------------------------------------------
  // Extended navigation helpers (Ctrl+Home/End, PageUp/Down)
  // ---------------------------------------------------------------------------

  const getFirstCellInTable = useCallback(
    (): CellPosition | null => {
      if (editableColumnIds.length === 0 || opts.rowCount === 0) return null;
      return { rowIndex: 0, columnId: editableColumnIds[0] };
    },
    [editableColumnIds, opts.rowCount],
  );

  const getLastCellInTable = useCallback(
    (): CellPosition | null => {
      if (editableColumnIds.length === 0 || opts.rowCount === 0) return null;
      return { rowIndex: opts.rowCount - 1, columnId: editableColumnIds[editableColumnIds.length - 1] };
    },
    [editableColumnIds, opts.rowCount],
  );

  const getCellPageDown = useCallback(
    (from: CellPosition, pageSize: number): CellPosition | null => {
      if (!editableColumnIds.includes(from.columnId)) return null;
      const target = Math.min(from.rowIndex + pageSize, opts.rowCount - 1);
      if (target === from.rowIndex) return null;
      return { rowIndex: target, columnId: from.columnId };
    },
    [editableColumnIds, opts.rowCount],
  );

  const getCellPageUp = useCallback(
    (from: CellPosition, pageSize: number): CellPosition | null => {
      if (!editableColumnIds.includes(from.columnId)) return null;
      const target = Math.max(from.rowIndex - pageSize, 0);
      if (target === from.rowIndex) return null;
      return { rowIndex: target, columnId: from.columnId };
    },
    [editableColumnIds],
  );

  return {
    activeCell,
    selectCell,
    clearSelection,
    isCellActive,
    isCellEditable,
    editableColumnIds,
    getNextEditableCell,
    getPrevEditableCell,
    getCellBelow,
    getCellAbove,
    getFirstInRow,
    getLastInRow,
    getFirstCellInTable,
    getLastCellInTable,
    getCellPageDown,
    getCellPageUp,
  };
}
